import React, { useMemo, useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiFileText, FiPrinter, FiDownload, FiCalendar } = FiIcons

const Transcripts = () => {
  const [selectedStudent, setSelectedStudent] = useState('Ava M.')
  const [schoolYear, setSchoolYear] = useState('2024-2025')
  const [semesterLabel, setSemesterLabel] = useState('Fall Semester')
  const [courses] = useState([
    { name: 'Mathematics II', grade: 'A', credits: 1.0 },
    { name: 'Biology', grade: 'B+', credits: 1.0 },
    { name: 'World Literature', grade: 'A-', credits: 1.0 },
    { name: 'US History', grade: 'A', credits: 1.0 },
  ])
  const [attendance] = useState({ present: 162, absent: 5, tardy: 2 })

  const totalCredits = useMemo(() => courses.reduce((sum, course) => sum + course.credits, 0), [courses])

  const exportPdf = () => {
    const transcriptText = `
Official Transcript
Student: ${selectedStudent}
Year: ${schoolYear} - ${semesterLabel}
Courses:
${courses.map(c => `${c.name}: ${c.grade} (${c.credits} credits)`).join('\n')}
Attendance: Present ${attendance.present}, Absent ${attendance.absent}, Tardy ${attendance.tardy}
`
    const blob = new Blob([transcriptText], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${selectedStudent}-transcript.pdf`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm uppercase tracking-wider text-blue-100">Records</p>
            <h1 className="text-3xl font-bold">Official Transcripts</h1>
            <p className="text-blue-100 mt-2">Generate print-friendly transcripts with credits, grades, and attendance.</p>
          </div>
          <SafeIcon icon={FiFileText} className="w-12 h-12 text-white opacity-80" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6 lg:col-span-2 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Student</p>
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              >
                <option value="Ava M.">Ava M.</option>
                <option value="Liam J.">Liam J.</option>
                <option value="Sofia R.">Sofia R.</option>
              </select>
            </div>
            <div>
              <p className="text-sm text-gray-600">School Year</p>
              <input
                type="text"
                value={schoolYear}
                onChange={(e) => setSchoolYear(e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                placeholder="2024-2025"
              />
            </div>
            <div>
              <p className="text-sm text-gray-600">Term Label</p>
              <input
                type="text"
                value={semesterLabel}
                onChange={(e) => setSemesterLabel(e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                placeholder="Fall Semester"
              />
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2 text-[#00AAFF]" />
                Course Records
              </h3>
              <span className="text-sm text-gray-500">Credits: {totalCredits.toFixed(1)}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {courses.map(course => (
                    <tr key={course.name}>
                      <td className="px-4 py-3 text-sm text-gray-800">{course.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{course.grade}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">{course.credits}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center px-4 py-2 bg-[#1B4C87] text-white rounded-lg hover:bg-[#163b68]"
            >
              <SafeIcon icon={FiPrinter} className="w-4 h-4 mr-2" />
              Print Friendly
            </button>
            <button
              onClick={exportPdf}
              className="inline-flex items-center px-4 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0088cc]"
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
              Download PDF
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Attendance Summary</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Present</span>
              <span className="font-semibold text-[#1B4C87]">{attendance.present}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Absent</span>
              <span className="font-semibold text-[#1B4C87]">{attendance.absent}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Tardy</span>
              <span className="font-semibold text-[#1B4C87]">{attendance.tardy}</span>
            </div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg text-sm text-[#1B4C87]">
            Transcripts include course completion, grades, credits, and attendance records with customizable term labels.
          </div>
        </div>
      </div>
    </div>
  )
}

export default Transcripts
